import { Button } from "@/shared/components/ui/button";
import { Plus } from "lucide-react";
import t from "@/lang/lang";
import { useVbsConfirmationCreateMutation } from "@/services/backend/vbs/confirmations/api/confirmation-api";
import { v4 } from "uuid";
import { useToast } from "@/shared/hooks/use-toast";
import { ReactNode, useEffect, useState } from "react";
import { Slot } from "radix-ui";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import { BranchSelectFrom } from "@/services/backend/branch/components/branch-select-from";
import { ServiceObjectSelectFrom } from "@/services/backend/customers/components/service-object-select-from";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Field, FieldLabel } from "@/shared/components/ui/field";
import {
  CommonDialogFooter,
  CommonDialogHeader,
} from "@/shared/components/dialog/dialog-common";

interface Props {
  processId: string;
  onCreated: (confirmationId: string) => void;
  trigger?: ReactNode;
}

export function CreateConfirmationButton({
  processId,
  onCreated,
  trigger,
}: Props) {
  const [open, setOpen] = useState(false);
  const [create, { isLoading, error, reset }] =
    useVbsConfirmationCreateMutation();
  const { data: process } = useProcessesProcessShowQuery({ id: processId });
  const { toast } = useToast();

  const branches = process?.branches ?? [];
  const serviceObjects = process?.serviceObjects ?? [];
  const [branchId, setBranchId] = useState(branches[0] ?? "");
  const [serviceObjectId, setServiceObjectId] = useState(
    serviceObjects[0] ?? "",
  );

  const disabled =
    !process || isLoading || branchId === "" || serviceObjectId === "";

  const doCreate = () => {
    if (disabled || !process?.customerId) {
      return;
    }

    const id = v4();
    create({
      id,
      processId: process.id,
      companyId: process.companyId,
      branchId,
      customerId: process.customerId,
      serviceObjectId,
    })
      .unwrap()
      .then((c) => {
        setOpen(false);
        onCreated(c.id);
        reset();
      });
  };

  useEffect(() => {
    if (error) {
      toast({ ...parseRTKQueryError(error), variant: "destructive" });
      reset();
    }
  }, [error, toast, reset]);

  if (branches.length === 1 && serviceObjects.length === 1) {
    if (trigger) {
      return <Slot.Root onClick={doCreate}>{trigger}</Slot.Root>;
    }
    return (
      <Button onClick={doCreate} disabled={disabled} size="sm">
        <Plus className="mr-2 h-5 w-5" />
        <span>{t("Auftragsbestätigung")}</span>
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button disabled={disabled} size="sm">
            <Plus className="mr-2 h-5 w-5" />
            <span>{t("Auftragsbestätigung")}</span>
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <CommonDialogHeader title={t("Auftragsbestätigung erstellen")} />
        {branches.length !== 1 && (
          <Field>
            <FieldLabel>{t("Niederlassung")}</FieldLabel>
            <BranchSelectFrom
              branchIds={branches}
              value={branchId}
              onValueChange={setBranchId}
            />
          </Field>
        )}
        {serviceObjects.length !== 1 && (
          <Field>
            <FieldLabel>{t("Service Objekt")}</FieldLabel>
            <ServiceObjectSelectFrom
              serviceObjectIds={serviceObjects}
              customerId={process?.customerId}
              value={serviceObjectId}
              onValueChange={setServiceObjectId}
            />
          </Field>
        )}
        <CommonDialogFooter
          isLoading={isLoading}
          disabled={disabled}
          onClickAction={doCreate}
        />
      </DialogContent>
    </Dialog>
  );
}
